<template>
	<div id="clientLandingPages">
		<JuniperPageHeader
			:back="'/juniper/clients/edit/' + ($route.params.id)"
			:title="client ? client.name : ''"
			subtitle="Landing Pages"
		>
			<button v-if="themeSupports" class="j-icon-btn" @click="openNewModal">
				<font-awesome-icon :icon="['fas', 'plus']" class="j-icon-btn-icon" />
				<span class="j-icon-btn-tooltip">New Landing Page</span>
			</button>
		</JuniperPageHeader>

		<div class="lp-section mb-4">
			<!-- Loading -->
			<div v-if="loading" class="loading-state">
				<div class="spinner-border text-primary" role="status">
					<span class="visually-hidden">Loading...</span>
				</div>
			</div>

			<!-- Theme does not support landing pages -->
			<div v-else-if="!themeSupports" class="empty-state">
				<div class="empty-state-content">
					<div class="empty-state-icon"><i class="fad fa-ban"></i></div>
					<h3 class="empty-state-title">Theme doesn't support Landing Pages</h3>
					<p class="empty-state-description">
						The active theme <code>{{ activeTheme }}</code> is missing
						<code>landing/sections/</code> templates or
						<code>additional_options.landings: true</code> in <code>conf.json</code>.
					</p>
				</div>
			</div>

			<template v-else>
				<!-- Search -->
				<div class="lp-toolbar">
					<div class="search-input-wrapper">
						<i class="fad fa-search search-icon"></i>
						<input
							type="text"
							class="search-input"
							placeholder="Search landing pages"
							v-model="search"
						/>
					</div>
					<div class="lp-toolbar-spacer"></div>
				</div>

				<!-- Table -->
				<div class="lp-table">
					<div class="table-header">
						<div class="header-cell">Title</div>
						<div class="header-cell">URL</div>
						<div class="header-cell">Status</div>
						<div class="header-cell">Updated</div>
						<div class="header-cell actions-col"></div>
					</div>

					<div v-if="filteredItems.length === 0" class="empty-state">
						<div class="empty-state-content">
							<div class="empty-state-icon"><i class="fad fa-folder-open"></i></div>
							<h3 class="empty-state-title">No Landing Pages</h3>
							<p class="empty-state-description">
								{{ search ? 'No results for your search.' : 'Create your first landing page to get started.' }}
							</p>
							<div v-if="!search" class="empty-state-actions">
								<button class="empty-state-btn" @click="openNewModal">
									<i class="fad fa-plus me-2"></i> Create Your First Landing Page
								</button>
							</div>
						</div>
					</div>

					<div
						v-for="lp in filteredItems"
						:key="lp.id"
						class="table-row"
					>
						<div class="table-cell">
							<router-link :to="customizerLink(lp)" class="lp-title-link">
								{{ lp.title }}
							</router-link>
						</div>
						<div class="table-cell">
							<span class="lp-slug">/{{ lp.slug }}/</span>
						</div>
						<div class="table-cell">
							<span class="status-badge" :class="statusClass(lp.status)">
								<i :class="statusIcon(lp.status)"></i>
								{{ lp.status }}
							</span>
						</div>
						<div class="table-cell">
							<span class="updated-date" :title="formatFull(lp.updated_at)">
								{{ formatRelative(lp.updated_at) }}
							</span>
						</div>
						<div class="table-cell actions-cell">
							<a
								v-if="lp.status === 'publish'"
								:href="externalUrl(lp)"
								target="_blank"
								rel="noopener"
								class="action-icon-btn action-icon-btn--sky"
								title="Open published page"
							>
								<i class="fad fa-globe"></i>
							</a>
							<router-link
								:to="customizerLink(lp)"
								class="action-icon-btn action-icon-btn--blue"
								title="Edit"
							>
								<i class="fad fa-pen"></i>
							</router-link>
							<button
								v-if="lp.status !== 'trashed'"
								class="action-icon-btn action-icon-btn--orange"
								title="Move to trash"
								@click="deleteLp(lp)"
							>
								<i class="fad fa-trash"></i>
							</button>
							<button
								v-else
								class="action-icon-btn action-icon-btn--teal"
								title="Restore"
								@click="restoreLp(lp)"
							>
								<i class="fad fa-undo"></i>
							</button>
						</div>
					</div>
				</div>
			</template>
		</div>

		<!-- New Landing Page modal -->
		<div v-if="newModalOpen" class="lp-modal-backdrop" @click.self="closeNewModal">
			<div class="lp-modal-dialog" role="dialog" aria-modal="true">
				<div class="lp-modal-content">
					<div class="lp-modal-header">
						<h5 class="modal-title mb-0">New Landing Page</h5>
						<button type="button" class="btn-close" @click="closeNewModal" aria-label="Close"></button>
					</div>
					<div class="lp-modal-body">
						<div class="mb-3">
							<label class="form-label">Title</label>
							<input
								type="text"
								class="form-control"
								v-model="newLp.title"
								@input="onTitleInput"
								placeholder="Summer Festival 2025"
								ref="titleInput"
							/>
						</div>
						<div class="mb-3">
							<label class="form-label">Slug (URL)</label>
							<div class="input-group">
								<span class="input-group-text">/</span>
								<input
									type="text"
									class="form-control"
									v-model="newLp.slug"
									@blur="checkSlug"
								/>
								<span class="input-group-text">/</span>
							</div>
							<small v-if="slugCheckMessage" :class="slugAvailable ? 'text-success' : 'text-danger'">
								{{ slugCheckMessage }}
							</small>
						</div>
					</div>
					<div class="lp-modal-footer">
						<button class="btn btn-secondary" @click="closeNewModal">Cancel</button>
						<button class="btn btn-primary" :disabled="!canCreate" @click="createLp">
							Create &amp; Customize
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import moment from 'moment-timezone'
import juniperApi from '../../../api/juniperApi'
import JuniperPageHeader from '../../../components/juniper/layout/JuniperPageHeader.vue'

export default {
	name: 'ClientLandingPages',
	components: { JuniperPageHeader },
	data() {
		return {
			loading: true,
			client: null,
			activeTheme: '',
			themeSupports: false,
			items: [],
			search: '',
			newModalOpen: false,
			newLp: { title: '', slug: '' },
			slugTouched: false,
			slugAvailable: false,
			slugCheckMessage: '',
		}
	},
	computed: {
		filteredItems() {
			const items = (this.items || []).filter(i => i.status !== 'trashed')
			if (!this.search) return items
			const q = this.search.toLowerCase()
			return items.filter(
				i => (i.title || '').toLowerCase().includes(q) || (i.slug || '').toLowerCase().includes(q)
			)
		},
		canCreate() {
			return this.newLp.title.trim() && this.newLp.slug.trim() && this.slugAvailable
		},
	},
	created() {
		this.loadClient()
	},
	mounted() {
		document.addEventListener('keydown', this.handleEscape)
	},
	beforeDestroy() {
		document.removeEventListener('keydown', this.handleEscape)
		document.body.classList.remove('lp-modal-open')
	},
	methods: {
		loadClient() {
			this.loading = true
			juniperApi
				.get('/clients/' + this.$route.params.id)
				.then(({ data }) => {
					this.client = data
					this.activeTheme = this.resolveTheme(data)
					return this.checkThemeSupport()
				})
				.then(() => this.loadItems())
				.catch(err => {
					console.error(err)
					this.loading = false
				})
		},
		resolveTheme(client) {
			if (client && client.options) {
				const opt = client.options.find(o => o.meta_key === 'theme_active')
				if (opt) return opt.meta_value
			}
			return client?.theme_active || client?.theme || 'default'
		},
		checkThemeSupport() {
			return juniperApi
				.post(`/themes/${this.activeTheme}/landing/sections`, {
					client_id: this.$route.params.id,
				})
				.then(({ data }) => {
					this.themeSupports = Array.isArray(data) && data.length > 0
				})
				.catch(() => {
					this.themeSupports = false
				})
		},
		loadItems() {
			juniperApi
				.get('/landing-pages', { params: { client_id: this.$route.params.id } })
				.then(({ data }) => {
					this.items = Array.isArray(data) ? data : data.data || []
				})
				.finally(() => {
					this.loading = false
				})
		},
		statusClass(status) {
			return {
				live: status === 'publish',
				preparing: status === 'draft',
				deleted: status === 'trashed',
			}
		},
		statusIcon(status) {
			if (status === 'publish') return 'fad fa-check-circle'
			if (status === 'draft') return 'fad fa-pencil-alt'
			return 'fad fa-trash'
		},
		formatRelative(d) {
			if (!d) return '—'
			return moment(d).fromNow()
		},
		formatFull(d) {
			if (!d) return ''
			return moment(d).format('MMM D, YYYY h:mm A')
		},
		customizerLink(lp) {
			return `/juniper/clients/edit/${this.$route.params.id}/landing-pages/${lp.id}`
		},
		externalUrl(lp) {
			if (!this.client) return '#'
			// Prefer staging URL when client is on staging
			let base
			if (this.client.staging && this.client.staging_url) {
				base = this.client.staging_url
			} else if (this.client.url) {
				base = this.client.url
			} else {
				return '#'
			}
			if (!base.startsWith('http')) base = 'https://' + base
			return `${base.replace(/\/$/, '')}/${lp.slug}/`
		},
		// ── Modal ──
		openNewModal() {
			this.newLp = { title: '', slug: '' }
			this.slugTouched = false
			this.slugAvailable = false
			this.slugCheckMessage = ''
			this.newModalOpen = true
			document.body.classList.add('lp-modal-open')
			this.$nextTick(() => {
				if (this.$refs.titleInput) this.$refs.titleInput.focus()
			})
		},
		closeNewModal() {
			this.newModalOpen = false
			document.body.classList.remove('lp-modal-open')
		},
		handleEscape(e) {
			if (e.key === 'Escape' && this.newModalOpen) {
				this.closeNewModal()
			}
		},
		onTitleInput() {
			if (!this.slugTouched) {
				this.newLp.slug = this.slugify(this.newLp.title)
				// Debounce-free auto-check so the Create button enables as soon
				// as the slug is available, without forcing the user to focus
				// the slug field manually.
				if (this._slugCheckTimer) clearTimeout(this._slugCheckTimer)
				this._slugCheckTimer = setTimeout(() => {
					if (this.newLp.slug) {
						this.checkSlugAuto()
					}
				}, 350)
			}
		},
		// Same as checkSlug() but does NOT mark the slug as user-touched.
		checkSlugAuto() {
			juniperApi
				.get('/landing-pages/check-slug', {
					params: { client_id: this.$route.params.id, slug: this.newLp.slug },
				})
				.then(({ data }) => {
					this.slugAvailable = data.available
					this.slugCheckMessage = data.available
						? 'Slug is available.'
						: data.reason === 'reserved'
						? 'This slug is reserved by the system.'
						: data.reason === 'page_collision'
						? 'A regular page already uses this slug.'
						: data.reason === 'lp_collision'
						? 'Another landing page already uses this slug.'
						: 'Slug is not available.'
				})
				.catch(() => {
					this.slugAvailable = false
					this.slugCheckMessage = 'Could not verify slug.'
				})
		},
		slugify(str) {
			return (str || '')
				.toLowerCase()
				.trim()
				.replace(/[^a-z0-9\s-]/g, '')
				.replace(/\s+/g, '-')
				.replace(/-+/g, '-')
				.replace(/^-|-$/g, '')
		},
		checkSlug() {
			this.slugTouched = true
			if (!this.newLp.slug) {
				this.slugAvailable = false
				this.slugCheckMessage = ''
				return
			}
			juniperApi
				.get('/landing-pages/check-slug', {
					params: { client_id: this.$route.params.id, slug: this.newLp.slug },
				})
				.then(({ data }) => {
					this.slugAvailable = data.available
					this.slugCheckMessage = data.available
						? 'Slug is available.'
						: data.reason === 'reserved'
						? 'This slug is reserved by the system.'
						: data.reason === 'page_collision'
						? 'A regular page already uses this slug.'
						: data.reason === 'lp_collision'
						? 'Another landing page already uses this slug.'
						: 'Slug is not available.'
				})
				.catch(() => {
					this.slugAvailable = false
					this.slugCheckMessage = 'Could not verify slug.'
				})
		},
		createLp() {
			juniperApi
				.post('/landing-pages', {
					client_id: this.$route.params.id,
					title: this.newLp.title,
					slug: this.newLp.slug,
					status: 'draft',
				})
				.then(({ data }) => {
					this.closeNewModal()
					this.$router.push(this.customizerLink(data))
				})
				.catch(err => {
					this.slugAvailable = false
					this.slugCheckMessage =
						err.response && err.response.data && err.response.data.errors
							? Object.values(err.response.data.errors).flat().join(' ')
							: 'Could not create landing page.'
				})
		},
		deleteLp(lp) {
			this.$swal
				.fire({
					title: 'Move to trash?',
					text: 'The static page will be removed from the live site.',
					icon: 'warning',
					showCancelButton: true,
					confirmButtonText: 'Trash',
					confirmButtonColor: '#d33',
				})
				.then(result => {
					if (!result.isConfirmed) return
					juniperApi.delete(`/landing-pages/${lp.id}`).then(() => this.loadItems())
				})
		},
		restoreLp(lp) {
			juniperApi.post(`/landing-pages/${lp.id}/restore`).then(() => this.loadItems())
		},
	},
}
</script>

<style scoped>
.lp-section {
	margin: 0 24px;
}
.lp-toolbar {
	display: flex;
	align-items: center;
	padding: 16px 0;
}
.lp-toolbar-spacer { flex: 1; }
.search-input-wrapper {
	position: relative;
	width: 320px;
}
.search-icon {
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	color: #9ca3af;
	font-size: 14px;
}
.search-input {
	width: 100%;
	padding: 8px 12px 8px 36px;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	font-size: 14px;
	outline: none;
	transition: border-color 0.2s ease;
	background: white;
}
.search-input:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.lp-table {
	background: white;
	border-radius: 8px;
	border: 1px solid #f3f4f6;
	overflow: hidden;
}
.table-header {
	display: grid;
	grid-template-columns: 2fr 2fr 130px 160px 160px;
	gap: 12px;
	padding: 12px 24px;
	background-color: #f9fafb;
	border-bottom: 1px solid #f3f4f6;
}
.header-cell {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
.table-row {
	display: grid;
	grid-template-columns: 2fr 2fr 130px 160px 160px;
	gap: 12px;
	align-items: center;
	padding: 16px 24px;
	border-bottom: 1px solid #f9fafb;
	transition: background-color 0.2s ease;
}
.table-row:hover {
	background-color: #f9fafb;
}
.table-row:last-child {
	border-bottom: none;
}
.table-cell {
	font-size: 14px;
	color: #111827;
}
.lp-title-link {
	color: #111827;
	font-weight: 500;
	text-decoration: none;
}
.lp-title-link:hover {
	color: #3b82f6;
}
.lp-slug {
	font-family: 'SF Mono', 'Fira Code', monospace;
	font-size: 13px;
	color: #6b7280;
}
.status-badge {
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 500;
	text-transform: capitalize;
	display: inline-flex;
	align-items: center;
	gap: 4px;
}
.status-badge.live {
	background-color: #dcfce7;
	color: #166534;
}
.status-badge.preparing {
	background-color: #fef3c7;
	color: #92400e;
}
.status-badge.deleted {
	background-color: #f3f4f6;
	color: #6b7280;
}
.updated-date {
	color: #6b7280;
	font-size: 13px;
}
.actions-cell {
	display: flex;
	gap: 4px;
	justify-content: flex-end;
}
.action-icon-btn {
	background: none;
	border: none;
	padding: 8px;
	border-radius: 4px;
	color: #9ca3af;
	cursor: pointer;
	transition: all 0.2s ease;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	text-decoration: none;
}
.action-icon-btn:hover {
	background-color: #f3f4f6;
}
.action-icon-btn--blue { color: #1d4ed8; }
.action-icon-btn--blue:hover { background-color: #dbeafe; }
.action-icon-btn--sky { color: #38bdf8; }
.action-icon-btn--sky:hover { background-color: #e0f2fe; }
.action-icon-btn--orange { color: #f97316; }
.action-icon-btn--orange:hover { background-color: #ffedd5; }
.action-icon-btn--teal { color: #0f766e; }
.action-icon-btn--teal:hover { background-color: #ccfbf1; }

/* Loading + Empty States */
.loading-state {
	display: flex;
	justify-content: center;
	padding: 80px 0;
}
.empty-state {
	padding: 60px 24px;
	text-align: center;
}
.empty-state-content {
	max-width: 400px;
	margin: 0 auto;
}
.empty-state-icon {
	font-size: 36px;
	color: #d1d5db;
	margin-bottom: 16px;
}
.empty-state-title {
	font-size: 18px;
	font-weight: 600;
	color: #111827;
	margin-bottom: 8px;
}
.empty-state-description {
	color: #6b7280;
	font-size: 14px;
	margin-bottom: 20px;
}
.empty-state-btn {
	background: #3b82f6;
	color: white;
	border: none;
	padding: 10px 20px;
	border-radius: 6px;
	font-weight: 500;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	transition: background 0.2s ease;
}
.empty-state-btn:hover {
	background: #2563eb;
}

/* Modal */
.lp-modal-backdrop {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.5);
	z-index: 1060;
	display: flex;
	align-items: flex-start;
	justify-content: center;
	padding: 80px 16px 16px;
	overflow-y: auto;
}
.lp-modal-dialog {
	width: 100%;
	max-width: 500px;
}
.lp-modal-content {
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
	display: flex;
	flex-direction: column;
}
.lp-modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px 20px;
	border-bottom: 1px solid #e5e7eb;
}
.lp-modal-body {
	padding: 20px;
}
.lp-modal-footer {
	padding: 12px 20px;
	border-top: 1px solid #e5e7eb;
	display: flex;
	justify-content: flex-end;
	gap: 8px;
}
</style>
<style>
body.lp-modal-open {
	overflow: hidden;
}
</style>
